// Client API for the staff-only Office Tasks scheduler (Phase 3). Online-only (the server holds
// all authority); the page gates on the connection state. NAMING: this is the interoffice "Tasks"
// module (`office_tasks`), never the bookings `Task`.

import { apiFetch } from './api';

export type OfficeTaskSource = 'manual' | 'email';
export type OfficeTaskStatus = 'open' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type OfficeTaskPriority = 'normal' | 'high';
export type OfficeTaskVisibility = 'personal' | 'base' | 'company'; // Phase 7 — who can see the task
export type OfficeTaskActionType =
  | 'note' | 'status_change' | 'assignment_change' | 'reminder' | 'completed' | 'reopened' | 'created_from_email'
  | 'visibility_change' | 'break_glass_access';

export const VISIBILITY_LABELS: Record<OfficeTaskVisibility, string> = {
  personal: 'Personal', base: 'Base', company: 'Company',
};

export interface PersonRef { id: string; full_name: string; preferred_name: string | null; pilot_code?: string | null; first_name?: string | null; last_name?: string | null; }

// A base for the picker/filter/chip (id + name + short_code for display). Mirrors the enquiries BaseRef.
export interface BaseRef { id: string; name: string; short_code: string; }

export interface OfficeTaskAssignee {
  id: string;
  user_id: string;
  assigned_at: string;
  user: PersonRef;
}

export interface OfficeTaskAction {
  id: string;
  actor_id: string | null;
  actor: PersonRef | null; // null = system-generated (reminders, email-origin)
  action_type: OfficeTaskActionType;
  body: string | null;
  created_at: string;
}

export interface OfficeTaskListItem {
  id: string;
  source: OfficeTaskSource;
  status: OfficeTaskStatus;
  priority: OfficeTaskPriority;
  title: string;
  description: string | null;
  due_at: string | null;
  base_id: string | null;
  base: BaseRef | null;
  visibility: OfficeTaskVisibility;
  created_by: string | null;
  created_by_email: string | null;
  completed_at: string | null;
  completed_by: string | null;
  last_activity_at: string;
  created_at: string;
  assignees: { user_id: string; user: PersonRef }[];
  creator: PersonRef | null;
  _count?: { actions: number };
  overdue: boolean;
  hidden?: boolean; // set only in owner break-glass discovery listing (not normally visible)
}

export interface OfficeTaskDetail extends OfficeTaskListItem {
  assignees: OfficeTaskAssignee[];
  actions: OfficeTaskAction[];
  broke_glass?: boolean; // set when this detail was fetched via owner break-glass
}

// Filters: status/assignee/base are all multi (any-of); assignee accepts 'me', base accepts 'none'.
export interface ListParams {
  statuses?: OfficeTaskStatus[];
  assignees?: string[];    // user ids and/or 'me'
  baseIds?: string[];      // base ids and/or 'none'
  q?: string;              // keyword search
  creators?: string[];     // creator/assignor user ids and/or 'me'
  created_from?: string;   // YYYY-MM-DD inclusive
  created_to?: string;     // YYYY-MM-DD inclusive
  tz?: string;             // IANA device timezone; sent only alongside a date filter (T2)
  overdue?: boolean;
  due_this_week?: boolean;
  unassigned?: boolean;
}

// One query-string builder shared by listTasks + breakGlassList so their param encoding can't drift.
function taskListQuery(p: ListParams): string {
  const q = new URLSearchParams();
  if (p.statuses?.length) q.set('status', p.statuses.join(','));
  if (p.assignees?.length) q.set('assignee', p.assignees.join(','));
  if (p.baseIds?.length) q.set('base_id', p.baseIds.join(','));
  if (p.q?.trim()) q.set('q', p.q.trim());
  if (p.creators?.length) q.set('creator', p.creators.join(','));
  if (p.created_from) q.set('created_from', p.created_from);
  if (p.created_to) q.set('created_to', p.created_to);
  // Only meaningful with a date bound — send the device tz so the day maps in the user's zone.
  if ((p.created_from || p.created_to) && p.tz) q.set('tz', p.tz);
  if (p.overdue) q.set('overdue', 'true');
  if (p.due_this_week) q.set('due_this_week', 'true');
  if (p.unassigned) q.set('unassigned', 'true');
  return q.toString();
}

export async function listTasks(p: ListParams = {}): Promise<OfficeTaskListItem[]> {
  const qs = taskListQuery(p);
  const res = await apiFetch<OfficeTaskListItem[]>(`/api/office-tasks${qs ? `?${qs}` : ''}`);
  return res.error || !res.data ? [] : res.data;
}

export async function getTask(id: string): Promise<OfficeTaskDetail | null> {
  const res = await apiFetch<OfficeTaskDetail>(`/api/office-tasks/${id}`);
  return res.error ? null : res.data;
}

// ── Break-glass (owner-only; Phase 7 §5) ─────────────────────────────────────────
// Owner reads an otherwise-hidden task after a fresh step-up. The server audits + writes a visible
// action row. Non-owner / bad step-up → 403.
export async function breakGlassTask(id: string, password: string): Promise<{ ok: boolean; data?: OfficeTaskDetail; error?: string }> {
  const res = await apiFetch<OfficeTaskDetail>(`/api/office-tasks/${id}/break-glass`, { method: 'POST', body: JSON.stringify({ step_up: { password } }) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true, data: res.data ?? undefined };
}
// Owner discovery: ALL tasks (hidden flagged) after a fresh step-up + one listing audit row.
export async function breakGlassList(password: string, p: ListParams = {}): Promise<{ ok: boolean; data?: OfficeTaskListItem[]; error?: string }> {
  const qs = taskListQuery(p);
  const res = await apiFetch<OfficeTaskListItem[]>(`/api/office-tasks/break-glass${qs ? `?${qs}` : ''}`, { method: 'POST', body: JSON.stringify({ step_up: { password } }) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true, data: res.data ?? [] };
}

// Homepage badge — counts only. is_site_owner drives the owner-only break-glass UI (Phase 7).
export interface OfficeTaskSummary { open: number; assigned_to_me: number; overdue: number; is_site_owner: boolean; }
export async function fetchSummary(): Promise<OfficeTaskSummary | null> {
  const res = await apiFetch<OfficeTaskSummary>('/api/office-tasks/summary');
  return res.error || !res.data ? null : res.data;
}

export interface CreateInput {
  title: string;
  description?: string | null;
  priority?: OfficeTaskPriority;
  due_at?: string | null;          // ISO with offset
  base_id?: string | null;
  assignee_user_ids?: string[];
  visibility?: OfficeTaskVisibility; // omit → server applies the 3c default
}

export async function createTask(input: CreateInput): Promise<{ ok: boolean; id?: string; error?: string }> {
  const res = await apiFetch<{ id: string }>('/api/office-tasks', { method: 'POST', body: JSON.stringify(input) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true, id: res.data?.id };
}

export async function patchTask(id: string, patch: { status?: OfficeTaskStatus; priority?: OfficeTaskPriority; title?: string; description?: string | null; due_at?: string | null; base_id?: string | null; visibility?: OfficeTaskVisibility }): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/office-tasks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export async function addAssignee(id: string, userId: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/office-tasks/${id}/assignees`, { method: 'POST', body: JSON.stringify({ user_id: userId }) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export async function removeAssignee(id: string, userId: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/office-tasks/${id}/assignees/${userId}`, { method: 'DELETE' });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

export async function addNote(id: string, body: string): Promise<{ ok: boolean; error?: string }> {
  const res = await apiFetch(`/api/office-tasks/${id}/actions`, { method: 'POST', body: JSON.stringify({ body }) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true };
}

// Admin → Tasks reminder settings (admin-only; the server enforces requireRole('admin')).
export interface ReminderSettings {
  due_soon_lead_hours: number;
  overdue_cadence_hours: number;
  no_due_date_idle_days: number;
  remind_creator: boolean;
  remind_unassigned: boolean;
}
export async function getSettings(): Promise<ReminderSettings | null> {
  const res = await apiFetch<ReminderSettings>('/api/office-tasks/settings');
  return res.error || !res.data ? null : res.data;
}
export async function patchSettings(patch: Partial<ReminderSettings>): Promise<{ ok: boolean; data?: ReminderSettings; error?: string }> {
  const res = await apiFetch<ReminderSettings>('/api/office-tasks/settings', { method: 'PATCH', body: JSON.stringify(patch) });
  return res.error ? { ok: false, error: res.error.message } : { ok: true, data: res.data ?? undefined };
}

// Staff for the assignee pickers (reuse the existing endpoint).
export interface StaffOption {
  id: string;
  full_name: string;
  first_name: string | null;
  last_name: string | null;
  preferred_name: string | null;
  pilot_code: string | null;
  home_base_id: string | null;
}
export async function fetchStaff(): Promise<StaffOption[]> {
  const res = await apiFetch<StaffOption[]>('/api/staff');
  return res.error || !res.data ? [] : res.data;
}

// Bases for the base picker/filter (reuse the existing endpoint; same shape enquiries uses).
export async function fetchBases(): Promise<BaseRef[]> {
  const res = await apiFetch<BaseRef[]>('/api/bases');
  return res.error || !res.data ? [] : res.data;
}
